import { useState } from 'react';

import { Permission, toEnglishDigits, toPersianDigits } from '@bookora/shared';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Code,
  Icon,
  Input,
  JalaliDate,
  Modal,
  OtpInput,
  QueryError,
  SkeletonBlock,
  SkeletonGroup,
  SkeletonLines,
  Space,
  StatusTag,
  Table,
  Tooltip,
  Typography,
  useConfirm,
  type ColumnsType,
} from '@bookora/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { listDeviceSessions, logoutEverywhere, revokeDeviceSession } from '@/api/endpoints/auth';
import { queryKeys } from '@/api/query-keys';
import type { DeviceSessionResponse } from '@/api/generated/model';
import type { DeviceSession } from '@/api/types';
import { useAuth } from '@/auth/token-store';
import { useOverlayState } from '@/hooks/use-overlay-state';
import { toast } from '@/lib/feedback';
import { useCan } from '@/permissions/use-can';

import { twoFactorApi } from './api';
import {
  RECOVERY_CODES_NOT_CONFIRMED,
  SECURITY_CODE_INCOMPLETE,
  TOTP_CODE_INCOMPLETE,
} from './reasons';
import { TableSkeleton } from '@/components/form-bits';
import { deviceIcon, deviceKindLabel, deviceLabel } from './device-label';
import { QrCode } from './qr-code';
import { SupportAccessCard } from './support-access-card';

const { Text, Paragraph } = Typography;

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  SECURITY — TOTP ENROLMENT AND DEVICE SESSIONS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ── RECOVERY CODES ARE SHOWN EXACTLY ONCE ───────────────────────────────────
 *
 * The server stores only their hashes. There is no endpoint that returns them
 * a second time, and this screen deliberately offers no affordance that looks
 * like one: no «نمایش دوباره», no collapsed panel, no copy of the list kept in
 * component state after the dialog closes. The only way back to a usable set is
 * `regenerate`, which mints a NEW set and invalidates the old one — and it says
 * so, because a user who believes they can re-read the codes will not write
 * them down.
 *
 * The dialog cannot be dismissed by clicking the mask or pressing Escape, and
 * its confirm button is disabled until the user ticks that they have saved
 * them. That is friction on purpose: losing both the phone and these codes
 * means a support intervention.
 *
 * ── THE OTP FIELD ───────────────────────────────────────────────────────────
 *
 * `OtpInput` from `@bookora/ui`, the single implementation. It normalises
 * Persian and Arabic-Indic digits to ASCII on typing AND paste. It must never
 * be given `REGEXP_ONLY_DIGITS`: JS `\d` is ASCII-only and `input-otp` tests
 * the pattern before `onChange`, so a Persian keyboard would silently not be
 * able to type into the field at all.
 */
export function SecurityPanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // Both dialogs stay mounted through their exit and are torn down after it.
  // The recovery codes are dropped in that same step, so the list still lives
  // exactly as long as the dialog that shows it — it just leaves with a fade.
  const enrolling = useOverlayState<true>();
  const recoveryCodes = useOverlayState<string[]>();

  const enabled = user?.twoFactorEnabled ?? false;
  // The log is the audit trail of who reached into this clinic: `audit.view`.
  const canSeeSupportAccess = useCan(Permission.AUDIT_VIEW);

  const refreshIdentity = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
  };

  const disable = useMutation({
    mutationFn: (code: string) => twoFactorApi.disable(code),
    onSuccess: () => {
      toast.success('تأیید دومرحله‌ای خاموش شد.');
      refreshIdentity();
    },
    onError: (error: unknown) => toast.error(error),
  });

  const regenerate = useMutation({
    mutationFn: (code: string) => twoFactorApi.regenerate(code),
    onSuccess: (result) => {
      recoveryCodes.show(result.recoveryCodes);
    },
    onError: (error: unknown) => toast.error(error),
  });

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="تأیید دومرحله‌ای"
        extra={
          <StatusTag
            tone={enabled ? 'success' : 'warning'}
            hint={
              enabled
                ? 'برای ورود به این حساب، بعد از کد پیامکی باید کد برنامهٔ رمز یک‌بارمصرف رو هم وارد کنید.'
                : 'ورود فقط با کد پیامکی انجام می‌شه.'
            }
          >
            {enabled ? 'فعال' : 'غیرفعال'}
          </StatusTag>
        }
      >
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ marginBlockEnd: 0 }}>
            ورود به بوکورا با کد پیامکی انجام می‌شه. برای حساب‌هایی که به پرونده‌ها، اطلاعات مالی یا
            فایل‌های خروجی دسترسی دارن، تأیید دومرحله‌ای لازم است. کلینیک می‌تونه اون رو برای همهٔ
            کارکنان الزامی کنه.
          </Paragraph>

          {enabled ? (
            <Space wrap>
              <CodeGatedButton
                label="ساخت دوبارهٔ کدهای بازیابی"
                title="ساخت دوبارهٔ کدهای بازیابی"
                confirmLabel="ساخت کدهای جدید"
                description="کدهای فعلی باطل می‌شن و کدهای جدید فقط یک بار نمایش داده می‌شن."
                pending={regenerate.isPending}
                onSubmit={(code) => regenerate.mutateAsync(code)}
              />
              <CodeGatedButton
                danger
                acceptsRecoveryCode
                label="خاموش‌کردن تأیید دومرحله‌ای"
                title="خاموش‌کردن تأیید دومرحله‌ای"
                confirmLabel="خاموش شود"
                description="بعد از این، ورود فقط با کد پیامکی انجام می‌شه و کدهای بازیابی فعلی باطل می‌شن."
                pending={disable.isPending}
                onSubmit={(code) => disable.mutateAsync(code)}
              />
            </Space>
          ) : (
            <Button
              type="primary"
              icon={<Icon name="shield" />}
              onClick={() => enrolling.show(true)}
            >
              فعال‌سازی تأیید دومرحله‌ای
            </Button>
          )}
        </Space>
      </Card>

      <DeviceSessionsCard
        onLogoutEverywhere={() => {
          void (async () => {
            const ok = await confirm({
              title: 'خروج از همهٔ دستگاه‌های دیگر؟',
              content:
                'از همهٔ دستگاه‌های دیگه خارج می‌شید. برای ورود دوباره، کد پیامکی لازمه. روی این دستگاه وارد می‌مونید.',
              okText: 'خارج شوند',
              danger: true,
            });
            if (ok) {
              try {
                const result = await logoutEverywhere(true);
                toast.success(
                  result.revoked > 0
                    ? `از ${toPersianDigits(result.revoked)} دستگاه دیگر خارج شدید.`
                    : 'دستگاه دیگری به این حساب وارد نبود.',
                );
                void queryClient.invalidateQueries({ queryKey: queryKeys.deviceSessions() });
              } catch (error) {
                toast.error(error);
              }
            }
          })();
        }}
      />

      {canSeeSupportAccess ? <SupportAccessCard /> : null}

      {enrolling.value ? (
        <EnrolDialog
          open={enrolling.open}
          onClose={enrolling.hide}
          key={`enrolling-${enrolling.session}`}
          afterClose={enrolling.afterClose}
          onEnabled={(codes) => {
            enrolling.hide();
            recoveryCodes.show(codes);
            refreshIdentity();
          }}
        />
      ) : null}

      {recoveryCodes.value ? (
        <RecoveryCodesDialog
          open={recoveryCodes.open}
          codes={recoveryCodes.value}
          onClose={recoveryCodes.hide}
          key={`recoveryCodes-${recoveryCodes.session}`}
          afterClose={recoveryCodes.afterClose}
        />
      ) : null}
    </Space>
  );
}

/* -------------------------------------------------------------------------- */
/* Enrolment                                                                  */
/* -------------------------------------------------------------------------- */

function EnrolDialog({
  open,
  onClose,
  afterClose,
  onEnabled,
}: {
  open: boolean;
  onClose: () => void;
  afterClose: () => void;
  onEnabled: (codes: string[]) => void;
}) {
  const [code, setCode] = useState('');

  const enrol = useQuery({
    queryKey: ['two-factor', 'enrolment'],
    // A POST behind `useQuery` because it is idempotent by design: re-running
    // it before verification REPLACES the pending secret, so re-opening the
    // dialog cannot leave two half-enrolments behind.
    queryFn: () => twoFactorApi.enrol(),
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  const verify = useMutation({
    mutationFn: (value: string) => twoFactorApi.verify(value),
    onSuccess: (result) => {
      toast.success('تأیید دومرحله‌ای فعال شد.');
      onEnabled(result.recoveryCodes);
    },
    onError: (error: unknown) => {
      toast.error(error);
      setCode('');
    },
  });

  return (
    <Modal
      open={open}
      afterClose={afterClose}
      title="فعال‌سازی تأیید دومرحله‌ای"
      cancelText="انصراف"
      onCancel={onClose}
      okText="تأیید و فعال‌سازی"
      okDisabledReason={code.length === 6 ? null : TOTP_CODE_INCOMPLETE}
      confirmLoading={verify.isPending}
      onOk={() => verify.mutate(code)}
      width={520}
    >
      {enrol.isLoading ? (
        // The QR code's own square, so the dialog does not grow by 220px the
        // moment the secret arrives.
        <SkeletonGroup label="در حال ساختن کد QR…">
          <Space orientation="vertical" size={14} align="center" style={{ width: '100%' }}>
            <SkeletonLines lines={1} style={{ inlineSize: '100%' }} />
            <SkeletonBlock width={220} height={220} />
            <SkeletonLines lines={2} style={{ inlineSize: '100%' }} />
          </Space>
        </SkeletonGroup>
      ) : null}
      {enrol.error ? <QueryError error={enrol.error} onRetry={() => void enrol.refetch()} /> : null}

      {enrol.data ? (
        <Space orientation="vertical" size={14} style={{ width: '100%' }} className="bk-enter-fade">
          <Paragraph style={{ marginBlockEnd: 0 }}>
            ۱. در برنامهٔ رمز یک‌بارمصرف (Google Authenticator، Authy، …) این کد رو اسکن کنید.
          </Paragraph>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <QrCode value={enrol.data.uri} size={220} />
          </div>

          <div>
            <Text type="secondary">اگر اسکن ممکن نبود، این کلید رو دستی در برنامه وارد کنید:</Text>
            {/* Thirty-two base32 characters are not something to retype on a
                phone; copying them is the whole point of showing them. */}
            <Space size={8} wrap>
              <Code value={enrol.data.secret} mono />
              <Button
                size="small"
                icon={<Icon name="copy" size={16} />}
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(enrol.data?.secret ?? '')
                    .then(() => toast.success('کلید کپی شد.'))
                    .catch(() => toast.warning('کپی خودکار ممکن نشد؛ کلید رو دستی بردارید.'));
                }}
              >
                کپی کلید
              </Button>
            </Space>
          </div>

          <Paragraph style={{ marginBlockEnd: 0 }}>۲. کد شش‌رقمی برنامه رو وارد کنید.</Paragraph>

          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={(value) => verify.mutate(value)}
            length={6}
            autoFocus
            ariaLabel="کد شش‌رقمی برنامهٔ رمز یک‌بارمصرف"
          />

          <Alert
            type="info"
            showIcon
            title="تأیید دومرحله‌ای بعد از تأیید این کد فعال می‌شه. اگر پنجره رو ببندید، می‌تونید مثل قبل وارد حسابتون بشید."
          />
        </Space>
      ) : null}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Recovery codes — shown once                                                */
/* -------------------------------------------------------------------------- */

function RecoveryCodesDialog({
  open,
  codes,
  onClose,
  afterClose,
}: {
  open: boolean;
  codes: string[];
  onClose: () => void;
  afterClose: () => void;
}) {
  const [saved, setSaved] = useState(false);

  const asText = codes.join('\n');

  return (
    <Modal
      open={open}
      afterClose={afterClose}
      title="کدهای بازیابی (فقط همین یک بار نمایش داده می‌شن)"
      cancelButtonProps={{ style: { display: 'none' } }}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      okText="کدها رو ذخیره کردم"
      okDisabledReason={saved ? null : RECOVERY_CODES_NOT_CONFIRMED}
      onOk={onClose}
      width={520}
    >
      <Space orientation="vertical" size={14} style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          title="این کدها دوباره نمایش داده نمی‌شن"
          description="اگر این کدها رو گم کنید، باید کدهای جدید بسازید. با ساخت کدهای جدید، کدهای قبلی باطل می‌شن."
        />

        <Paragraph type="secondary" style={{ marginBlockEnd: 0 }}>
          اگر به گوشی‌تون دسترسی نداشتید، می‌تونید به‌جای کد برنامه از یکی از این کدها استفاده کنید.
          هر کد فقط یک بار قابل استفاده است. کدها رو در جای امن نگه دارید.
        </Paragraph>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'var(--bk-space-2)',
            padding: 'var(--bk-space-3)',
            borderRadius: 'var(--bk-panel-radius-control)',
            border: '1px solid var(--bk-border)',
            background: 'var(--bk-bg-subtle)',
          }}
        >
          {codes.map((value) => (
            <Code key={value} value={value} mono />
          ))}
        </div>

        <Space wrap>
          <Button
            icon={<Icon name="copy" />}
            onClick={() => {
              void navigator.clipboard
                ?.writeText(asText)
                .then(() => toast.success('کدها کپی شد.'))
                .catch(() => toast.warning('کپی خودکار ممکن نشد؛ کدها رو دستی بردارید.'));
            }}
          >
            کپی همه
          </Button>
          <Button
            icon={<Icon name="download" />}
            onClick={() => {
              const blob = new Blob([`${asText}\n`], { type: 'text/plain;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const anchor = document.createElement('a');
              anchor.href = url;
              anchor.download = 'bookora-recovery-codes.txt';
              anchor.click();
              URL.revokeObjectURL(url);
            }}
          >
            دانلود فایل متنی
          </Button>
        </Space>

        {/* The kit's themed checkbox, not the browser's own (OS blue, no dark
            mode) in the most security-critical dialog in the panel. */}
        <Checkbox checked={saved} onChange={(event) => setSaved(event.target.checked)}>
          این کدها رو در جای امنی ذخیره کردم.
        </Checkbox>
      </Space>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* A button that first asks for a live authenticator code                     */
/* -------------------------------------------------------------------------- */

/**
 * `acceptsRecoveryCode` mirrors the backend, which is not symmetric:
 * `POST /auth/two-factor/disable` takes a TOTP code OR a recovery code (a
 * person whose phone is gone must still be able to turn the factor off), while
 * `POST /auth/two-factor/recovery-codes` takes a live TOTP code only. The
 * dialog used to be a six-cell digits-only `OtpInput` for both while telling
 * the user a recovery code would do — a recovery code (`ABCD-EFGH-JKLM`)
 * cannot be typed into that field at all.
 */
function CodeGatedButton({
  label,
  title,
  confirmLabel,
  description,
  pending,
  danger = false,
  acceptsRecoveryCode = false,
  onSubmit,
}: {
  label: string;
  title: string;
  /** The dialog's own button: what pressing it does, not «تأیید». */
  confirmLabel: string;
  description: string;
  pending: boolean;
  danger?: boolean;
  acceptsRecoveryCode?: boolean;
  /** Resolves when the mutation succeeded; rejects to keep the dialog open. */
  onSubmit: (code: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const ready = acceptsRecoveryCode ? code.replace(/[\s-]/g, '').length >= 6 : code.length === 6;

  async function submit(): Promise<void> {
    try {
      await onSubmit(code);
      // The typed code is cleared after the dialog has faded (`afterClose`),
      // not under it.
      setOpen(false);
    } catch {
      // The mutation's own `onError` has already toasted; a wrong code keeps
      // the dialog — and the typed digits — on screen so it can be retried.
    }
  }

  return (
    <>
      <Button danger={danger} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Modal
        open={open}
        afterClose={() => setCode('')}
        destroyOnHidden
        title={title}
        cancelText="انصراف"
        onCancel={() => setOpen(false)}
        okText={confirmLabel}
        okDisabledReason={
          ready ? null : acceptsRecoveryCode ? SECURITY_CODE_INCOMPLETE : TOTP_CODE_INCOMPLETE
        }
        confirmLoading={pending}
        onOk={() => {
          void submit();
        }}
        okButtonProps={{ danger }}
      >
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">{description}</Text>
          {acceptsRecoveryCode ? (
            <>
              <Text>
                برای ادامه، کد فعلی برنامهٔ رمز یک‌بارمصرف یا یکی از کدهای بازیابی رو وارد کنید.
              </Text>
              <Input
                value={code}
                // Persian keyboard digits are folded here, as `OtpInput` does
                // for the six-cell field; letters of a recovery code pass
                // through and the server normalises case and dashes.
                onChange={(event) => setCode(toEnglishDigits(event.target.value).trim())}
                aria-label="کد تأیید دومرحله‌ای"
                dir="ltr"
                className="bk-ltr-field"
                classNames={{ input: 'bk-ltr-field' }}
                style={{ textAlign: 'start', letterSpacing: '0.12em' }}
                autoFocus
                autoComplete="one-time-code"
                maxLength={20}
                placeholder="123456 / ABCD-EFGH-JKLM"
              />
            </>
          ) : (
            <>
              <Text>برای ادامه، کد فعلی برنامهٔ رمز یک‌بارمصرف رو وارد کنید.</Text>
              <OtpInput
                value={code}
                onChange={setCode}
                length={6}
                autoFocus
                ariaLabel="کد تأیید دومرحله‌ای"
              />
            </>
          )}
        </Space>
      </Modal>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Devices                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A row of `GET /auth/sessions`. The shell's hand-written `DeviceSession` does
 * not carry `deviceKind` yet; the generated response does, so the field is
 * read from there and stays optional until the two agree.
 */
type DeviceRow = DeviceSession & Partial<Pick<DeviceSessionResponse, 'deviceKind'>>;

function DeviceSessionsCard({ onLogoutEverywhere }: { onLogoutEverywhere: () => void }) {
  const queryClient = useQueryClient();

  const sessions = useQuery({
    queryKey: queryKeys.deviceSessions(),
    queryFn: ({ signal }): Promise<DeviceRow[]> => listDeviceSessions(signal),
    staleTime: 30_000,
  });

  const otherDevices = (sessions.data ?? []).filter((row) => !row.current).length;

  const revoke = useMutation({
    mutationFn: (id: string) => revokeDeviceSession(id),
    onSuccess: () => {
      toast.success('این دستگاه از حساب خارج شد.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.deviceSessions() });
    },
    onError: (error: unknown) => toast.error(error),
  });

  const columns: ColumnsType<DeviceRow> = [
    {
      title: 'دستگاه',
      dataIndex: 'deviceName',
      render: (name: string, row) => {
        const icon = deviceIcon(row.deviceKind);
        const kind = deviceKindLabel(row.deviceKind);
        return (
          <Space size={8} wrap>
            {icon ? <Icon name={icon} size={20} label={kind ?? undefined} /> : null}
            {/* The raw browser string, for whoever is checking a strange
                sign-in with support — on hover, as it always was, but through
                the kit bubble rather than a native title. It is extra detail
                behind a readable name, and the name is what is announced. */}
            <Tooltip
              kind="description"
              title={
                row.userAgent && row.userAgent !== deviceLabel(name) ? (
                  <bdi dir="ltr">{row.userAgent}</bdi>
                ) : (
                  ''
                )
              }
            >
              <Text strong>{deviceLabel(name)}</Text>
            </Tooltip>
            {row.current ? <StatusTag tone="success">این دستگاه</StatusTag> : null}
            {row.impersonated ? (
              <StatusTag
                tone="warning"
                hint="این ورود مربوط به پشتیبانی بوکوراست که برای رسیدگی به درخواست شما وارد حساب شده."
              >
                ورود پشتیبانی
              </StatusTag>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'نشانی اینترنتی',
      dataIndex: 'ip',
      // Useful to someone checking a strange sign-in, noise on a phone.
      responsive: ['md'],
      render: (ip: string | null) => (ip ? <Code value={ip} /> : <Text type="secondary">—</Text>),
    },
    {
      title: 'آخرین فعالیت',
      dataIndex: 'lastSeenAt',
      render: (value: string) => <JalaliDate value={value} />,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, row) =>
        row.current ? null : (
          <Button
            size="small"
            danger
            type="text"
            aria-label={`خروج از دستگاه ${deviceLabel(row.deviceName)}`}
            loading={revoke.isPending && revoke.variables === row.id}
            onClick={() => revoke.mutate(row.id)}
          >
            خروج از این دستگاه
          </Button>
        ),
    },
  ];

  return (
    <Card
      title="دستگاه‌های واردشده"
      extra={
        // A danger button that would do nothing is hidden: with this device
        // the only one signed in there is nobody else to sign out.
        otherDevices > 0 ? (
          <Button danger onClick={onLogoutEverywhere}>
            خروج از همهٔ دستگاه‌های دیگر
          </Button>
        ) : sessions.data ? (
          <Text type="secondary">دستگاه دیگری وارد نشده است.</Text>
        ) : null
      }
    >
      {sessions.isLoading ? <TableSkeleton rows={3} label="در حال بارگذاری دستگاه‌ها…" /> : null}
      {sessions.error ? (
        <QueryError error={sessions.error} onRetry={() => void sessions.refetch()} />
      ) : null}
      {sessions.data ? (
        <Table<DeviceRow>
          rowKey="id"
          pagination={false}
          dataSource={sessions.data}
          columns={columns}
          scroll={{ x: 560 }}
          locale={{ emptyText: 'دستگاهی وارد این حساب نشده.' }}
        />
      ) : null}
    </Card>
  );
}
